<template>
    <form class="container row align-items-center bg-white px-0 py-0 mx-auto shadow-sm mt-2" @submit.prevent="handleSubmit">
        <div class="col-lg-2 col-sm-6 col-12 p-2 form-column xs-no-border">
            <div class="row px-3 pr-md-2">
                <div class="col-10 px-0">
                    <div class="text-sm text-muted px-2">
                        {{ $t('components.taxi_search_form.from') }}:
                    </div>

                    <v-select
                        :options="startPlaces"
                        :placeholder="$t('components.taxi_search_form.from')"
                        :loading="isStartPlacesLoading"
                        :class="{ 'is-filled': parameters.start_place }"
                        :title="parameters.start_place ? parameters.start_place.start_place : ''"
                        label="start_place"
                        v-model="parameters.start_place"
                        @search="handleSearchStartPlaces"
                        @input="handlePickStartPlace"
                    >
                        <template v-slot:no-options="{ search, searching }">
                            <p>
                                <strong class="text-larger" v-html="$t('components.taxi_search_form.search_no_results')" />
                                <span v-html="$t('components.taxi_search_form.search_no_results2', { email: officeEmail })" />
                            </p>
                        </template>

                        <template #option="{ start_place, start_is_airport }">
                            <div class="place-search-dropdown-option" :class="{ 'is-airport': start_is_airport }">
                                {{ start_place }}
                            </div>
                        </template>
                    </v-select>
                </div>

                <div class="col-2 px-0 text-right">
                    <button type="button" class="btn light p-1 exchange-places-button" @click="swapLocations">
                        <font-awesome-icon icon="exchange-alt" />
                    </button>
                </div>
            </div>
        </div>

        <div class="col-lg-2 col-sm-6 col-12 p-2 form-column sm-no-border">
            <div class="text-sm text-muted px-2">
                {{ $t('components.taxi_search_form.to') }}:
            </div>

            <v-select
                :options="endPlaces"
                :placeholder="$t('components.taxi_search_form.to')"
                :loading="isEndPlacesLoading"
                :class="{ 'is-filled': parameters.end_place }"
                :title="parameters.end_place ? parameters.end_place.end_place : ''"
                label="end_place"
                ref="endPlacesSelect"
                v-model="parameters.end_place"
                @search="handleSearchEndPlaces"
            >
                <template v-slot:no-options="{ search, searching }">
                    <p>
                        <strong class="text-larger" v-html="$t('components.taxi_search_form.search_no_results')" />
                        <span v-html="$t('components.taxi_search_form.search_no_results2', { email: officeEmail })" />
                    </p>
                </template>

                <template #option="{ end_place, end_is_airport }">
                    <div class="place-search-dropdown-option" :class="{ 'is-airport': end_is_airport }">
                        {{ end_place }}
                    </div>
                </template>
            </v-select>
        </div>

        <div class="col-lg-2 col-sm-6 col-12 p-2 form-column xs-no-border">
            <div class="row px-2">
                <div class="col-12">
                    <div class="text-sm text-muted">
                        {{ $t('components.taxi_search_form.departure_date') }}:
                    </div>
                </div>
            </div>
            <div class="row pr-3">
                <div class="col-12">
                    <date-picker
                        :placeholder="$t('components.taxi_search_form.date_pick')"
                        value-type="YYYY-MM-DD"
                        :disabled-date="notBeforeToday"
                        ref="departureDatePicker"
                        v-model="parameters.departure_date"
                        format="dd DD MMM"
                        :lang="datePickerLang"
                    />

                    <div class="plus-icon" @click="handleDepartureDateClick">
                        <font-awesome-icon icon="plus" />
                    </div>
                </div>
            </div>
        </div>

        <div class="col-lg-2 col-sm-6 col-12 p-2 form-column sm-no-border">
            <div class="row px-2">
                <div class="col-12">
                    <div class="text-sm text-muted">
                        {{ $t('components.taxi_search_form.departure_time') }}:
                    </div>
                </div>
            </div>
            <div class="row pr-3">
                <div class="col-12">
                    <vue-timepicker
                        :minute-interval="5"
                        hide-clear-button
                        :placeholder="$t('components.taxi_search_form.add_time')"
                        ref="departureTimePicker"
                        v-model="parameters.departure_time"
                    />

                    <div class="plus-icon" @click="handleDepartureTimeClick">
                        <font-awesome-icon icon="plus" />
                    </div>
                </div>
            </div>
        </div>

        <div class="col-lg-2 col-sm-12 col-12 px-2 pb-2 form-column border-0">
            <div class="row px-2">
                <div class="col-12">
                    <div class="text-sm text-muted">
                        {{ $t('passengers') }}:
                    </div>
                </div>
            </div>
            <div class="row passengers-select-row">
                <div class="col-12">
                    <PassengersSelect :passengers.sync="passengers" />
                </div>
            </div>
        </div>

        <div class="col-lg-2 col-sm-12 col-12 px-0 h-100">
            <button type="submit" class="btn btn-primary w-100 h-100 py-lg-4 py-md-2">
                {{ $t('components.taxi_search_form.submit') }}
            </button>
        </div>
    </form>
</template>

<script>
    import DatePicker from 'vue2-datepicker';
    import VueTimepicker from 'vue2-timepicker';
    import 'vue2-datepicker/index.css';

    import plLang from 'vue2-datepicker/locale/pl';
    import enLang from 'vue2-datepicker/locale/en';

    import { debounce as _debounce } from 'lodash-es';
    import toastr from 'toastr';

    import PassengersSelect from '../PassengersSelect';
    import { mapGetters } from 'vuex';
    import backofficeRepository from '@repositories/BackofficeRepository';

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    export default {
        components: {
            DatePicker,
            VueTimepicker,
            PassengersSelect,
        },

        props: {
            offerType: {
                type: String,
                required: true,
            },

            splitAdultsChildren: {
                type: Boolean,
                default: false,
            },

            initialParams: {
                type: Object,
                default: null,
            },
        },

        data() {
            return {
                currentLanguage: window.APP.locale,

                startPlaces: [],
                endPlaces: [],
                isStartPlacesLoading: false,
                isEndPlacesLoading: false,

                parameters: {
                    start_place: null,
                    end_place: null,
                    departure_date: null,
                    departure_time: '',
                },

                passengers: null,

                //current user location got from the browser, defaults to Szczecin
                geoCurrentPosition: {coords: {latitude: 53.42, longitude: 14.56}},
            };
        },

        computed: {
            ...mapGetters({
                officeEmail: 'constants/officeEmail',
            }),

            datePickerLang() {
                if (this.currentLanguage === 'pl') {
                    return plLang;
                }

                return enLang;
            },

            passengersTotalQuantity() {
                let total = 0;

                if (this.passengers) {
                    this.passengers.forEach((passenger) => {
                        total += passenger.quantity;
                    });
                }

                return total;
            },
        },

        created() {
            //ask user for location
            if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition(position => this.geoCurrentPosition = position);
            }

            this.parameters.departure_date = this.formatDate(today);

            if (this.splitAdultsChildren) {
                this.passengers = [
                    {
                        type: 'adult',
                        quantity: 1,
                        min: 1,
                    },
                    {
                        type: 'child',
                        quantity: 0,
                        min: 0,
                    },
                ];
            } else {
                this.passengers = [
                    {
                        type: 'passenger',
                        quantity: 1,
                        min: 1,
                    },
                ];
            }

            if (this.initialParams) {
                if (this.initialParams.startPlaces) {
                    this.startPlaces = this.initialParams.startPlaces;
                }

                if (this.initialParams.endPlaces) {
                    this.endPlaces = this.initialParams.endPlaces;
                }

                if (this.initialParams.parameters.start_place) {
                    this.parameters.start_place = this.initialParams.parameters.start_place;
                }

                if (this.initialParams.parameters.end_place) {
                    this.parameters.end_place = this.initialParams.parameters.end_place;
                }


                if (this.initialParams.parameters.departure_date) {
                    this.parameters.departure_date = this.initialParams.parameters.departure_date;
                }

                if (this.initialParams.parameters.departure_time) {
                    this.parameters.departure_time = this.initialParams.parameters.departure_time;
                }

                if (this.initialParams.parameters.passengers) {
                    this.initialParams.parameters.passengers.forEach((passengerData) => {
                        this.passengers.forEach((passenger) => {
                            if (passengerData.type === passenger.type) {
                                passenger.quantity = passengerData.quantity;
                            }
                        });
                    });
                }
            }
        },

        methods: {
            handleSearchStartPlaces: _debounce(function (search, loading) {
                return this.fetchStartPlaces(search, loading);
            }, 500),

            handleSearchEndPlaces: _debounce(function (search, loading) {
                return this.fetchEndPlaces(search, loading);
            }, 500),

            fetchStartPlaces(search, loading) {
                this.startPlaces = [];
                if (search.length <= 2) {
                    return;
                }

                this.endPlaces = [];
                this.parameters.end_place = null;

                this.isStartPlacesLoading = true;
                backofficeRepository.searchMapAutocomplete(search, this.geoCurrentPosition.coords.latitude, this.geoCurrentPosition.coords.longitude).then((places) => {
                    this.startPlaces = places.map((place) => {
                        return {
                            placeId: place.placeId,
                            start_place: place.description,
                            start_is_airport: !place.types.includes('geocode'),
                        };
                    });
                    this.isStartPlacesLoading = false;
                    this.$emit('startPlacesFetched', this.startPlaces);
                });
            },

            handlePickStartPlace(value) {
                if (!value) {
                    return;
                }

                //focus endPlaces vue select if its not selected yet
                if(!this.parameters.end_place){
                    this.$nextTick(() => {
                        const input = this.$refs.endPlacesSelect.$el.querySelector('input');
                        if (input) {
                            input.focus();
                        }
                    });
                }
            },

            fetchEndPlaces(search, loading) {
                this.endPlaces = [];
                if (search.length <= 2) {
                    return;
                }
                this.parameters.end_place = null;

                this.isEndPlacesLoading = true;
                backofficeRepository.searchMapAutocomplete(search, this.geoCurrentPosition.coords.latitude, this.geoCurrentPosition.coords.longitude).then((places) => {
                    this.endPlaces = places.map((place) => {
                        return {
                            placeId: place.placeId,
                            end_place: place.description,
                            end_is_airport: !place.types.includes('geocode'),
                        };
                    });
                    this.isEndPlacesLoading = false;
                    this.$emit('endPlacesFetched', this.endPlaces);
                });
            },

            handleSubmit() {
                if (!this.parameters.start_place) {
                    toastr.error(this.$t('field_is_required', {field: this.$t('from')}));

                    return;
                }

                if (!this.parameters.end_place) {
                    toastr.error(this.$t('field_is_required', {field: this.$t('to')}));

                    return;
                }

                if (!this.parameters.departure_date) {
                    toastr.error(this.$t('field_is_required', {field: this.$t('departure_date')}));

                    return;
                }

                if (!this.parameters.departure_time) {
                    toastr.error(this.$t('field_is_required', {field: this.$t('departure_time')}));

                    return;
                }

                if (this.passengersTotalQuantity === 0) {
                    toastr.error(this.$t('validation.at_least_one_passenger_required'));

                    return;
                }

                this.$emit(
                    'search',
                    Object.assign(
                        {},
                        this.parameters,
                        {
                            start_place: this.parameters.start_place.start_place,
                            start_place_id: this.parameters.start_place.placeId,
                            start_is_airport: this.parameters.start_place.start_is_airport,
                            end_place: this.parameters.end_place.end_place,
                            end_place_id: this.parameters.end_place.placeId,
                            end_is_airport: this.parameters.end_place.end_is_airport,
                            passengers: this.passengers,
                            passengers_number: this.passengersTotalQuantity,
                        }
                    )
                );
            },

            swapLocations() {
                const newStartPlace = {
                    placeId: this.parameters.end_place.placeId,
                    start_place: this.parameters.end_place.end_place,
                    start_is_airport: this.parameters.end_place.end_is_airport,
                };

                const newEndPlace = {
                    placeId: this.parameters.start_place.placeId,
                    end_place: this.parameters.start_place.start_place,
                    end_is_airport: this.parameters.start_place.start_is_airport,
                };

                this.parameters.start_place = newStartPlace;
                this.parameters.end_place = newEndPlace;
            },

            notBeforeToday(date) {
                return date < today;
            },

            handleDepartureDateClick() {
                this.$refs.departureDatePicker.openPopup();
            },

            handleDepartureTimeClick() {
                this.$refs.departureTimePicker.showDropdown = true;
            },
        },

    };
</script>

<style scoped>
    .text-larger{
        font-size: 110%;
    }
    .form-column:not(:last-child) {
        border-right: 1px solid lightgray;
    }

    .v-select {
        height: 2.2rem;
    }

    .v-select >>> .vs__selected {
        text-overflow: ellipsis;
        width: 90%;
        display: inline-block;
        white-space: nowrap;
        overflow: hidden;
    }

    .v-select >>> .vs__no-options {
        padding: 0.2rem 0.4rem;
    }

    .v-select.vs--open >>> .vs__selected {
        display: none;
    }

    .v-select >>> .vs__clear {
        display: none !important;
    }
    .v-select >>> .vs__dropdown-menu {
        width: fit-content;
        border-left: none;
    }
    .v-select >>> .vs__dropdown-toggle {
        border: none;
    }
    .v-select >>> .vs__dropdown-toggle .vs__selected-options{
        width: 100%;
    }
    .v-select >>> .vs__dropdown-toggle .vs__selected-options .vs__search::placeholder {
        color:#5f6a70;
    }
    .v-select >>> .vs__open-indicator {
        display: none;
    }
    .v-select.is-filled:not(.vs--open) >>> .vs__search {
        height: 0;
        padding: 0;
    }
    .vue__time-picker >>> input.display-time {
        border: none;
        padding-top: 0px;
        width: 94%;
    }
    .vue__time-picker >>> input.display-time:focus {
        outline: none;
    }
    .vue__time-picker >>> input.display-time::placeholder {
        color:#5f6a70;
    }
    .mx-datepicker >>> .mx-input-wrapper .mx-input {
        border:none;
        padding-right: 20px;
        box-shadow: none;
        pointer-events: none;
    }
    .mx-datepicker >>> .mx-input-wrapper .mx-input::placeholder {
        font-size: 16px;
        color: #5f6a70;
    }
    .mx-datepicker >>> .mx-input-wrapper .mx-icon-calendar {
        display: none;
    }

    .plus-icon {
        position: absolute;
        top: 0.1rem;
        right: 0px;
        cursor: pointer;
        opacity: 0.7;
    }


    @media (min-width: 991px) {
        .passengers-select-row {
            height: 1.6rem;
        }
    }

    .exchange-places-button {
        margin-top: 1.2rem;
    }

    @media (max-width: 991px) {
        .sm-no-border {
            border: none !important;
        }
    }

    @media (max-width: 767px) {
        .xs-no-border {
            border: none !important;
        }
    }
</style>
